import type { CSSProperties, MouseEvent } from 'react';
import { getBezierPath, BaseEdge, EdgeLabelRenderer } from '@xyflow/react';
import type { EdgeProps } from '@xyflow/react';
import { Tooltip, Button, Space } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';

import { useDeleteEdge, useInsertNodeBetween } from '../../hooks/builder';

import styles from './DeletableEdge.module.scss';
import { AppEdge } from '../../types/node';

const DeletableEdge = ({
  source,
  sourceX,
  sourceY,
  target,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
}: EdgeProps<AppEdge>) => {
  const { _ } = useLingui();
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const deleteEdge = useDeleteEdge();
  const insertNodeBetween = useInsertNodeBetween();

  const onDeleteEdge = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    deleteEdge(source, target);
  };

  const onInsertNode = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    insertNodeBetween(source, target);
  };

  // Highlight edge when connected node is hovered
  const edgeStyle = {
    ...style,
    stroke: data?.nodeHovered ? '#1677ff' : style.stroke,
    strokeWidth: data?.nodeHovered ? 3 : style.strokeWidth || 2,
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={edgeStyle} />
      <EdgeLabelRenderer>
        <div
          style={{
            '--label-x': `${labelX}px`,
            '--label-y': `${labelY}px`,
          } as CSSProperties}
          className={`${styles.label} nodrag nopan nowheel`}
        >
          <Space.Compact>
            <Tooltip title={_(msg`Insert node`)}>
              <Button
                className={`${styles.edgeButton} ${
                  data?.hovered ? styles.edgeButtonVisible : ''
                }`}
                shape="circle"
                size="small"
                icon={<PlusOutlined />}
                onClick={onInsertNode}
                aria-label={_(msg`Insert node`)}
              />
            </Tooltip>
            <Tooltip title={_(msg`Delete edge`)}>
              <Button
                className={`${styles.edgeButton} ${
                  data?.hovered ? styles.edgeButtonVisible : ''
                }`}
                shape="circle"
                size="small"
                danger
                icon={<CloseOutlined />}
                onClick={onDeleteEdge}
                aria-label={_(msg`Delete edge`)}
              />
            </Tooltip>
          </Space.Compact>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export default DeletableEdge;
